"use client";

import * as React from "react";
import { Button as ShadButton, ButtonProps } from "@/components/ui/shadcn/button";
import { cn } from "@/lib/utils";
import { ArrowRight } from "lucide-react";
import * as LucideIcons from "lucide-react";
import Link from "next/link";

interface CustomButtonProps extends ButtonProps {
  text?: string;
  href?: string;
  isBlank?: boolean;
  showArrow?: boolean;
  isDisabled?: boolean;
  className?: string;
  onClick?: () => void;
  iconName?: string;
  children?: React.ReactNode;
  overrideWidth?: boolean;
}

const CustomButton = React.forwardRef<HTMLButtonElement, CustomButtonProps>(
  (
    {
      text,
      href,
      isBlank = false,
      showArrow = false,
      isDisabled = false,
      className,
      onClick,
      iconName,
      children,
      overrideWidth = false,
      ...rest
    },
    ref,
  ) => {
    const Icon = iconName ? (LucideIcons as Record<string, unknown>)[iconName] as React.ComponentType<{ className?: string }> | undefined : null;
    const target = isBlank ? "_blank" : "_self";

    // ## Common button content

    const buttonBaseClasses =
      `inline-flex justify-center items-center py-2 px-6 text-base font-600 text-center text-white rounded-lg bg-red-600 focus:bg-red-700 hover:bg-red-700 focus:ring-4 focus:ring-red-300 dark:focus:ring-red-900 group ${!overrideWidth ? 'w-full' : ''}`;

    const buttonJSX = (
      <ShadButton
        ref={ref}
        className={cn(buttonBaseClasses, className)}
        disabled={isDisabled}
        onClick={onClick}
        {...rest}
      >
        {Icon && <Icon className="mr-2 h-4 w-4" />}
        {children || text}
        {showArrow && (
          // transition-transform group-hover:translate-x-1 (4px movement)
          // group-hover:animate-arrow-slide (using animation defined in tailwind.config.ts)
          <ArrowRight
            className="ml-2 h-3.5 w-3.5 rtl:rotate-180 transition-transform group-hover:translate-x-1"
            aria-hidden="true"
          />
        )}
      </ShadButton>
    );

    // ## If href is provided, render as a Link-wrapped button
    if (href) {
      return (
        <Link
          href={href}
          target={target}
          rel={isBlank ? "noopener noreferrer" : undefined}
          className={`inline-flex ${!overrideWidth ? 'w-full' : ''}`}
        >
          {buttonJSX}
        </Link>
      );
    }

    // ## Otherwise, render as a standalone button
    return buttonJSX;
  },
);

CustomButton.displayName = "CustomButton";

export default CustomButton;
